import { EditOutlined } from '@ant-design/icons';
import { Company } from '../models/company-profile.model';
import {
  CompanyMetrics,
  companyWebsiteLabel,
  getCompanyCompletion,
} from './company-workspace.utils';

interface Props {
  company: Company;
  editing: boolean;
  isAdmin: boolean;
  logo: string;
  metrics: CompanyMetrics;
  onEdit: () => void;
  onSelectSection: (key: string) => void;
}

interface MetricProps {
  label: string;
  value: number | null;
  detail: string;
  onClick: () => void;
}

const Metric = ({ label, value, detail, onClick }: MetricProps) => (
  <button className="company-metric" type="button" onClick={onClick}>
    <span>{label}</span>
    <strong>{value === null ? '—' : value.toLocaleString()}</strong>
    <small>{value === null ? 'Temporarily unavailable' : detail}</small>
  </button>
);

const CompanyWorkspaceHeader = ({
  company,
  editing,
  isAdmin,
  logo,
  metrics,
  onEdit,
  onSelectSection,
}: Props) => {
  const completion = getCompanyCompletion(company, Boolean(logo), metrics);
  const companyInitials =
    company.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'CO';

  return (
    <>
      <header className="company-page-heading">
        <div>
          <span>Workspace administration</span>
          <h1>Company</h1>
          <p>Manage your organization, people, and recruiting access.</p>
        </div>
      </header>

      <section className="company-identity" aria-label="Company summary">
        <div className="company-identity__brand">
          <div className="company-identity__logo" aria-hidden>
            {logo ? <img src={logo} alt="" /> : companyInitials}
          </div>
          <div>
            <h2>{company.name || 'Company profile'}</h2>
            <p>
              {companyWebsiteLabel(company.website) ||
                'Add a company website to complete the profile'}
            </p>
          </div>
        </div>

        <div className="company-identity__status">
          <div>
            <strong>{completion}%</strong>
            <span>Profile complete</span>
          </div>
          <div>
            <strong>{editing ? 'Editing' : 'Overview'}</strong>
            <span>Workspace mode</span>
          </div>
          {isAdmin && !editing ? (
            <button
              className="company-secondary-action"
              type="button"
              onClick={onEdit}
            >
              <EditOutlined aria-hidden />
              Edit company
            </button>
          ) : null}
        </div>
      </section>

      <section className="company-metrics" aria-label="Company metrics">
        <Metric
          label="Clients"
          value={metrics.clients}
          detail="Active client accounts"
          onClick={() => onSelectSection('2')}
        />
        <Metric
          label="Team members"
          value={metrics.users}
          detail="Workspace access"
          onClick={() => onSelectSection('3')}
        />
        <Metric
          label="Candidates"
          value={metrics.candidates}
          detail="Talent pool"
          onClick={() => onSelectSection('4')}
        />
        <Metric
          label="Active jobs"
          value={metrics.activeJobs}
          detail="Open positions"
          onClick={() => onSelectSection('jobs')}
        />
      </section>
    </>
  );
};

export default CompanyWorkspaceHeader;
